import { Button as BaseButton } from '@base-ui/react/button'
import React from 'react'
import styled, { css } from 'styled-components'
import {
  DISABLED,
  dangerStyles,
  sizeStyles,
  spinnerStyles,
  squareSizes,
  variantStyles,
} from 'sutra/components/Button/button-styles'
import type {
  ButtonProps,
  ButtonSize,
  ButtonVariant,
} from 'sutra/components/Button/button-types'
import { Sparkle } from 'sutra/components/Icons'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'

// ── Styled base ───────────────────────────────────────────────────────────────

const StyledButton = styled(BaseButton)<{
  $variant: ButtonVariant
  $size: ButtonSize
  $height?: string
  $danger: boolean
  $block: boolean
  $iconOnly: boolean
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 500;
  border: 1.5px solid transparent;
  border-radius: ${radii.sm}px;
  cursor: pointer;
  transition:
    background ${motion.duration.base}ms ${motion.ease},
    color ${motion.duration.base}ms ${motion.ease},
    border-color ${motion.duration.base}ms ${motion.ease},
    box-shadow ${motion.duration.fast}ms ${motion.ease};
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);

  ${DISABLED} {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &[aria-busy='true'] {
    cursor: progress;
  }

  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant }) => variantStyles[$variant]}
  ${({ $danger, $variant }) => $danger && dangerStyles[$variant]}

  ${({ $block }) =>
    $block &&
    css`
      display: flex;
      width: 100%;
    `}

  ${({ $iconOnly, $size, $height }) =>
    $iconOnly &&
    css`
      width: ${$height ?? squareSizes[$size]};
      height: ${$height ?? squareSizes[$size]};
      padding: 0;
    `}

  ${({ $height, $iconOnly }) =>
    $height &&
    !$iconOnly &&
    css`
      height: ${$height};
      padding-top: 0;
      padding-bottom: 0;
    `}
`

const Spinner = styled.span`
  ${spinnerStyles}
`

// ── Component ─────────────────────────────────────────────────────────────────

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      $variant = 'primary',
      $size = 'sm',
      $height,
      variant,
      size,
      height,
      loading = false,
      icon,
      danger = false,
      block = false,
      disabled,
      children,
      ...rest
    },
    ref,
  ) => {
    const resolvedVariant = variant ?? $variant
    const resolvedSize = size ?? $size
    const resolvedHeight = height ?? $height
    const hasLabel = React.Children.toArray(children).length > 0
    const leading = loading ? (
      <Spinner aria-hidden="true" />
    ) : (
      (icon ?? (resolvedVariant === 'askYogi' ? <Sparkle /> : null))
    )
    return (
      <StyledButton
        ref={ref}
        $variant={resolvedVariant}
        $size={resolvedSize}
        $height={resolvedHeight}
        $danger={danger}
        $block={block}
        $iconOnly={!hasLabel && (loading || icon != null)}
        aria-busy={loading || undefined}
        disabled={disabled || loading}
        focusableWhenDisabled={loading}
        {...rest}
      >
        {leading}
        {children}
      </StyledButton>
    )
  },
)

Button.displayName = 'Button'
